import { useEffect, useState, type RefObject } from 'react';

/**
 * Viewport-top offset at which a masthead counts as collapsed (ui-ux.md §3):
 * the header's bottom edge crossing this line flips the compact sticky title.
 * The one implementation — do not re-derive the 56 threshold per screen.
 */
export const COLLAPSE_THRESHOLD_PX = 56;

export interface CollapsingHeader {
  /** True once the sentinel has scrolled above the threshold line. */
  collapsed: boolean;
}

/**
 * The shared collapsing-masthead signal (ui-ux.md §3), feeding
 * `<Masthead collapsed={...}>`. The caller renders a content-free sentinel
 * element at the header's bottom edge and hands its ref in:
 *
 * ```tsx
 * const sentinelRef = useRef<HTMLDivElement>(null);
 * const { collapsed } = useCollapsingHeader(sentinelRef);
 * …
 * <div ref={sentinelRef} aria-hidden className="h-px w-full" />
 * ```
 *
 * An IntersectionObserver watches the sentinel against a root inset by
 * `COLLAPSE_THRESHOLD_PX` — the sentinel leaving that root means the header's
 * bottom edge has crossed the threshold. There is deliberately no `scroll`
 * listener here: the observer replaces a per-event `getBoundingClientRect()`
 * layout read, which is the exact anti-pattern ui-ux.md §3 calls out.
 *
 * SSR-safe (no `window`/`IntersectionObserver` access outside the effect) and
 * fails open: where IntersectionObserver is unavailable the header simply
 * never collapses — the expanded masthead stays put.
 */
export function useCollapsingHeader(sentinelRef: RefObject<HTMLElement | null>): CollapsingHeader {
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) setCollapsed(!entry.isIntersecting);
      },
      { rootMargin: `-${COLLAPSE_THRESHOLD_PX}px 0px 0px 0px`, threshold: 0 },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [sentinelRef]);

  return { collapsed };
}
